import React from "react";
import { Modal } from "../../../components/ui";
import { fmt, fmtDate } from "../../../lib/formatters";
import { FileText, Package, AlertTriangle, Box } from "lucide-react";

export const DamagedItemDetailModal = ({ damagedItem, onClose }) => {
  if (!damagedItem) return null;

  const invoiceNo = damagedItem.returnRef?.invoiceNo || damagedItem.returnRef?.originalInvoiceNo || damagedItem.invoice || "N/A";

  return (
    <Modal
      isOpen={!!damagedItem}
      onClose={onClose}
      title={
        <div className="flex flex-col gap-0.5">
          <span className="text-[14px] font-bold text-white font-serif tracking-widest uppercase">
            Verve Kitchens
          </span>
          <span className="text-[9px] text-slate-300 font-semibold tracking-widest uppercase">
            Damaged Item Details
          </span>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Header Information */}
        <div className="flex items-center justify-between pb-4 border-b border-slate-100">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-orange-50 text-orange-500 rounded-xl flex items-center justify-center border border-orange-100">
              <AlertTriangle size={24} />
            </div>
            <div>
              <h3 className="text-[16px] font-semibold text-slate-800 font-['Poppins']">
                {invoiceNo}
              </h3>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-[12px] font-medium text-slate-500">
                  Date: {damagedItem.date ? fmtDate(damagedItem.date) : 'N/A'}
                </span>
              </div>
            </div>
          </div>
          <div className="capitalize">
            <span className="text-[10px] font-medium text-orange-600 bg-orange-50 px-2 py-0.5 rounded border border-orange-100 uppercase tracking-tight">
              {damagedItem.type || 'UNKNOWN'}
            </span>
          </div>
        </div>

        {/* Summary Grid */}
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 rounded-xl bg-slate-50 border border-slate-100 space-y-3">
            <div className="flex items-center gap-2 text-slate-400 mb-1">
              <FileText size={14} />
              <span className="text-[11px] font-bold text-slate-400 tracking-wider uppercase">Reference</span>
            </div>
            <div>
              <p className="text-slate-700 font-bold text-[12px] tracking-wide mb-1">Invoice / Return Ref</p>
              <p className="text-[12px] font-medium text-slate-800 truncate leading-snug">
                {invoiceNo}
              </p>
            </div>
            <div>
              <p className="text-slate-700 font-bold text-[12px] tracking-wide mb-1">Source Type</p>
              <p className="text-[12px] font-medium text-slate-800 truncate leading-snug uppercase">{damagedItem.type || 'N/A'}</p>
            </div>
            <div>
              <p className="text-slate-700 font-bold text-[12px] tracking-wide mb-1">Party</p>
              <p className="text-[12px] font-medium text-slate-800 truncate leading-snug">
                {damagedItem.returnRef?.vendor?.name || damagedItem.returnRef?.vendor?.vendorName || damagedItem.returnRef?.supplier?.name || damagedItem.returnRef?.party || damagedItem.returnRef?.customer?.name || damagedItem.returnRef?.project?.customerName || "—"}
              </p>
            </div>
          </div>

          <div className="p-4 rounded-xl bg-slate-50 border border-slate-100 space-y-3">
            <div className="flex items-center gap-2 text-slate-400 mb-1">
              <Box size={14} />
              <span className="text-[11px] font-bold text-slate-400 tracking-wider uppercase">Damage Summary</span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-slate-700 font-bold text-[12px] tracking-wide mb-1">Total Items</p>
                <p className="text-[12px] font-medium text-slate-800 truncate leading-snug">{damagedItem.totalDamagedQty || 0}</p>
              </div>
              <div>
                <p className="text-slate-700 font-bold text-[12px] tracking-wide mb-1">Total Value</p>
                <p className="text-[12px] font-medium text-[#f97316] truncate leading-snug">{fmt(damagedItem.totalDamagedValue || 0)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Damaged Items List */}
        <div className="space-y-3">
          <h4 className="text-[11px] font-bold text-slate-400 tracking-wider uppercase flex items-center gap-2">
            <Package size={14} className="text-slate-400" />
            Damaged Items
          </h4>
          <div className="border border-slate-100 rounded-xl overflow-hidden bg-white shadow-sm">
            <table className="w-full text-left">
              <thead className="bg-slate-50">
                <tr className="border-b border-slate-100">
                  <th className="px-4 py-2.5 text-slate-700 font-bold text-[13px] tracking-wide">Item</th>
                  <th className="px-4 py-2.5 text-slate-700 font-bold text-[13px] tracking-wide">Reason</th>
                  <th className="px-4 py-2.5 text-slate-700 font-bold text-[13px] tracking-wide text-center">Qty</th>
                  <th className="px-4 py-2.5 text-slate-700 font-bold text-[13px] tracking-wide text-right">Value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {damagedItem.damagedItems?.map((item, index) => {
                  const qty = item.damagedQty || 0;
                  const rate = item.rate || 0;
                  const amount = qty * rate;

                  return (
                    <tr key={index} className="hover:bg-slate-50/50">
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-[12px] font-medium text-slate-800 truncate leading-snug">
                            {item.itemName || item.item?.name || item.item?.itemName || 'Unknown Item'}
                          </p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-[11px] font-medium text-slate-500 uppercase tracking-tight">
                          {item.reason || 'Damage reported'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-[12px] font-medium text-slate-800">{qty}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-[12px] font-medium text-[#f97316] truncate leading-snug">{fmt(amount)}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex gap-3 pt-4 border-t border-slate-100">
          <button
            className="flex-1 px-4 py-2.5 bg-slate-50 text-slate-600 font-medium text-[13px] rounded-lg border border-slate-200 hover:bg-slate-100 transition-all"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};
